// @flow
import React from 'react';
import PlacesAutocomplete, {
  geocodeByAddress
} from 'react-places-autocomplete';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';

import './StreetAddress.scss';

export type Props = {
  placeholder: string,
  type: string,
  setPickupAddress: (type: string) => void,
  setDropoffAddress: (type: string) => void,
  isGooglePlacesAvailable: Boolean,
  pickupAddress: string,
  initialValue: string,
  pickupDropoffSame: boolean,
  dropoffAddress: string,
  google: ?Object,
  autoFocus: boolean,
  intl: IntlShape,
  externalValet: string,
  cleanClutchError: () => void,
  errorMessage: string,
  errorAriaLabel: string,
  inputAriaLabel: string,
  isClutchPartialValet: boolean
};

type State = {
  address: string,
  error: boolean
};

export default class StreetAddress extends React.Component<Props, State> {
  static block = 'street-address';

  constructor(props: Props) {
    super(props);
    this.state = {
      address: this.props.initialValue,
      error: false
    };
  }
  handleStreetChange = (address: string) => {
    const {
      type,
      pickupAddress,
      dropoffAddress,
      setPickupAddress,
      setDropoffAddress,
      cleanClutchError,
      errorMessage,
      pickupDropoffSame
    } = this.props;
    const newState = { ...this.state, address };
    if (type === 'pick-up' && pickupAddress) {
      setPickupAddress('');
      newState.address = '';
    }

    if (type === 'drop-off' && dropoffAddress) {
      setDropoffAddress('');
      newState.address = '';
    }

    (errorMessage || pickupDropoffSame) && cleanClutchError();
    this.setState(newState);
  };

  handleStreetSelect = (address: string) => {
    geocodeByAddress(address)
      .then(results => {
        results.forEach(arrayAddress => {
          const listAddress = arrayAddress.address_components;
          listAddress.forEach(addressSuggested => {
            if (address.includes(addressSuggested.long_name)) {
              const {
                type,
                setPickupAddress,
                setDropoffAddress,
                pickupDropoffSame,
                isClutchPartialValet
              } = this.props;
              if (pickupDropoffSame) {
                setPickupAddress(address);
                setDropoffAddress(address);
              } else {
                if (isClutchPartialValet) {
                  setDropoffAddress('');
                }
                if (type === 'pick-up') {
                  setPickupAddress(address);
                }
                if (type === 'drop-off') {
                  setDropoffAddress(address);
                }
              }
              this.setState({ address, error: false });
            }
          });
        });
      })
      .catch(e => {
        /* eslint-disable no-console */
        console.error(e);
        /* eslint-enable no-console */
      });
  };

  handleStreetError = (status: string, clearSuggestions: () => void) => {
    if (status === 'ZERO_RESULTS') {
      this.setState({ error: true });
      clearSuggestions();
    }
  };

  render() {
    const {
      placeholder,
      google,
      autoFocus,
      intl,
      errorMessage,
      errorAriaLabel,
      inputAriaLabel,
      externalValet
    } = this.props;
    const ns = StreetAddress.block;
    const isMapsLoaded = google ? Boolean(google.maps) : false;
    const marginTop = externalValet ? 'mt-3' : 'mt-4';
    let inputClassName = `${ns}__input-street location-search-input pl-4 pb-2`;
    let iconClassName = `${ns}__icon-street icon icon-location`;

    if (errorMessage) {
      inputClassName += ` ${ns}__input-street--error`;
      iconClassName += ` ${ns}__icon-street--error`;
    }
    return isMapsLoaded ? (
      <PlacesAutocomplete
        value={this.state.address}
        onChange={this.handleStreetChange}
        onSelect={this.handleStreetSelect}
        onError={this.handleStreetError}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <div className={`${ns} ${marginTop}`}>
            <span className={iconClassName} />
            <input
              autoFocus={autoFocus}
              className={inputClassName}
              {...getInputProps({
                placeholder
              })}
              aria-label={inputAriaLabel}
            />
            {errorMessage && (
              <div
                className={`${ns}__error-message`}
                tabIndex={0}
                aria-label={errorAriaLabel}
              >
                {errorMessage}
              </div>
            )}
            <div className={`${ns}__autocomplete-dropdown-container`}>
              {loading && (
                <div>{intl.formatMessage({ id: 'c8.pick.up.loading' })}</div>
              )}
              {suggestions.map(suggestion => {
                return (
                  <div
                    key={suggestion.id}
                    className={`${ns}__suggestion-item-street py-3`}
                    {...getSuggestionItemProps(suggestion, {})}
                  >
                    <span>{suggestion.description}</span>
                  </div>
                );
              })}
              {this.state.error &&
                suggestions.length === 0 && (
                  <div className={`${ns}__error-item-street py-3`}>
                    <span>
                      {intl.formatMessage({
                        id: 'c8.pick.up.no.results.please.enter.a.valid.address'
                      })}
                    </span>
                  </div>
                )}
            </div>
          </div>
        )}
      </PlacesAutocomplete>
    ) : (
      // Workaround for iOS: autoFocus don't run immediately cause PlacesAutocomplete
      // component don't render immediately for this reason the keyboard don't display; however,
      // the following input enables the keyboard while the component is rendered.
      <div className={marginTop}>
        <span className={iconClassName} />
        <input
          autoFocus={autoFocus}
          className={inputClassName}
          placeholder={placeholder}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/molecules/StreetAddress/StreetAddress.js